/**
 * @file Entity: an id plus a bag of components.
 *
 * An entity holds no behaviour and no rendering state of its own. It is a
 * recyclable handle: {@link ../ecs/World.js World} pools instances and calls
 * `_recycle()` when the id is reused, so gameplay code must never cache an
 * entity object across its destruction.
 */

import { bitOf } from './Component.js';

/**
 * @typedef {object} EntityHost
 * @property {(entity: Entity) => void} onEntityStructureChanged Invoked whenever
 *   the entity's component mask changes so cached queries can be invalidated.
 */

export class Entity {
  /**
   * @param {number} id Unique, monotonically increasing identifier.
   * @param {EntityHost} host Owner notified on structural changes.
   */
  constructor(id, host) {
    /** @type {number} */
    this.id = id;
    /** @type {EntityHost} */
    this.host = host;
    /** @type {number} Bitmask of the component types currently attached. */
    this.mask = 0;
    /** @type {boolean} False once scheduled for destruction. */
    this.alive = true;
    /** @type {Map<Function, object>} */
    this.components = new Map();
    /** @type {string} Optional label, useful while debugging. */
    this.name = '';
  }

  /**
   * Attaches a component instance. Re-adding the same type replaces it.
   * @template T
   * @param {T} component Component instance (its constructor must be registered).
   * @returns {T} The component, so callers can keep a direct reference.
   */
  add(component) {
    const Type = /** @type {Function} */ (component.constructor);
    this.components.set(Type, component);
    this.mask |= bitOf(Type);
    this.host.onEntityStructureChanged(this);
    return component;
  }

  /**
   * @template T
   * @param {new (...args: any[]) => T} Type Component constructor.
   * @returns {T | undefined} The attached instance, if any.
   */
  get(Type) {
    return /** @type {T | undefined} */ (this.components.get(Type));
  }

  /**
   * @param {Function} Type Component constructor.
   * @returns {boolean} True when the component is attached.
   */
  has(Type) {
    return (this.mask & bitOf(Type)) !== 0;
  }

  /**
   * Detaches a component type.
   * @param {Function} Type Component constructor.
   * @returns {boolean} True when something was actually removed.
   */
  remove(Type) {
    if (!this.components.delete(Type)) return false;
    this.mask &= ~bitOf(Type);
    this.host.onEntityStructureChanged(this);
    return true;
  }

  /**
   * Tests the entity against a query mask.
   * @param {number} mask Combined component bits.
   * @returns {boolean}
   */
  matches(mask) {
    return (this.mask & mask) === mask;
  }

  /**
   * Wipes the entity so the underlying object can back a new id.
   * Called by the world; never call it from gameplay code.
   * @param {number} id New identifier.
   * @returns {void}
   * @internal
   */
  _recycle(id) {
    this.id = id;
    this.mask = 0;
    this.alive = true;
    this.name = '';
    this.components.clear();
  }
}
